// src/components/Purchases.jsx

/**
 * Purchases.jsx (Module Achats - FEDIS)
 * =====================================
 * Objectif:
 * - Centraliser la gestion des achats sous un seul module, avec un flux clair:
 *   Bon de commande (BC)  ->  Réception / Bon d'achat (BA)
 *
 * Fonctionnel (côté utilisateur):
 * 1) Bons de commande (BC)
 *    - Permet de préparer/valider une commande fournisseur (prévisionnelle).
 *    - Un BC ne modifie pas le stock: c'est une intention d'achat.
 *    - Sert de base pour suivre ce qui doit être reçu.
 *
 * 2) Réception / Bon d'achat (BA)
 *    - Permet d'enregistrer l'achat réel à la réception de la marchandise.
 *    - Le BA MODIFIE le stock (stock++) car les produits ont été effectivement reçus.
 *    - Génère un PDF de bon d'achat (preuve interne / traçabilité).
 *
 * Structure UI:
 * - Le module expose 2 vues principales (via onglets + sous-routes):
 *   - /purchases/orders     : Bons de commande (liste/gestion)
 *   - /purchases/orders/:id : Détail d’un bon de commande
 *   - /purchases/receive    : Réception / Bon d'achat (création + PDF)
 *
 * Intégration API:
 * - BC: endpoints côté serveur (ex: /api/purchase-orders...)
 * - BA: POST /api/purchases + GET /api/purchases/:external_id/pdf
 *
 * Notes:
 * - UX simple: on commande d’abord (BC), puis on réceptionne (BA).
 * - Ce fichier est le point d'entrée central du module Achats.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { apiFetch, apiFetchBlob, downloadBlob } from '../api'
import { useStore } from '../store/index.js'
import { Pencil, Trash2, ChevronDown, ChevronUp, XCircle } from 'lucide-react'

import PurchaseOrders from './Purchase/PurchaseOrders.jsx'
import PurchaseOrderView from './Purchase/PurchaseOrderView.jsx'

// Helper affichage montants
const money = (v) => Number(v ?? 0).toLocaleString('fr-FR')
const moneyXOF = (v) => money(v) + ' XOF'

// Téléchargement garanti navigateur
function forceDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export default function Purchases({ products = [], reloadProducts }) {
  const location = useLocation()

  const tabBase = 'px-4 py-2 rounded-lg text-sm font-semibold border transition'
  const tabInactive = 'border-border text-content-primary hover:bg-surface-primary'
  const tabActive = 'border-slate-200 bg-slate-50 text-slate-800'

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-extrabold text-content-primary">Achats</h1>
        <p className="text-sm text-content-secondary mt-1">
          Bons de commande (BC) puis réception / bon d’achat (BA).
        </p>
      </div>

      {/* Tabs (✅ liens RELATIFS pour matcher les routes internes) */}
      <div className="flex flex-wrap gap-2 mb-5">
        <NavLink
          to="orders"
          className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
        >
          Bons de commande (BC)
        </NavLink>

        <NavLink
          to="receive"
          className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabInactive}`}
        >
          Réception / Bon d’achat (BA)
        </NavLink>

        {/* petit indicateur path */}
        <div className="ml-auto text-xs text-content-muted self-center hidden md:block">
          {location.pathname}
        </div>
      </div>

      {/* Sous-routes internes au module */}
      <Routes>
        <Route index element={<Navigate to="receive" replace />} />

        {/* BA */}
        <Route
          path="receive"
          element={<PurchasesBA products={products} reloadProducts={reloadProducts} />}
        />

        {/* BC */}
        <Route path="orders" element={<PurchaseOrders products={products} />} />
        <Route path="orders/:id" element={<PurchaseOrderView />} />

        {/* fallback module */}
        <Route path="*" element={<Navigate to="receive" replace />} />
      </Routes>
    </div>
  )
}

/* =========================================================
 * Modal correction d'une ligne de BA
 * ========================================================= */
function CorrectPurchaseLineModal({ externalId, line, onClose, onSuccess }) {
  const [productSearch, setProductSearch] = useState('')
  const [productResults, setProductResults] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [qty, setQty] = useState(String(Number(line.qty_ordered || line.qty || 1)))
  const [unitCost, setUnitCost] = useState(String(Number(line.unit_cost || 0)))
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState('')
  const searchTimer = useRef(null)

  useEffect(() => {
    if (!productSearch.trim()) { setProductResults([]); return }
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      setSearching(true)
      try {
        const data = await apiFetch(`/api/products?search=${encodeURIComponent(productSearch)}&limit=8`)
        setProductResults(Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [])
      } catch { setProductResults([]) }
      finally { setSearching(false) }
    }, 300)
  }, [productSearch])

  function selectProduct(p) {
    setSelectedProduct(p)
    setProductSearch(p.name)
    setProductResults([])
  }

  async function handleSubmit() {
    setErr('')
    const parsedQty  = Math.round(Number(qty))
    const parsedCost = Number(unitCost)
    if (!parsedQty || parsedQty <= 0) return setErr('Quantité invalide')
    if (!Number.isFinite(parsedCost) || parsedCost < 0) return setErr('Coût invalide')

    const body = {}
    if (selectedProduct && selectedProduct.id !== line.product_id) body.product_id = selectedProduct.id
    if (parsedQty   !== Number(line.qty_ordered || line.qty)) body.qty = parsedQty
    if (parsedCost  !== Number(line.unit_cost))               body.unit_cost = parsedCost
    if (!Object.keys(body).length) return setErr('Aucune modification détectée')

    setSubmitting(true)
    try {
      await apiFetch(`/api/purchases/${externalId}/lines/${line.id}`, {
        method: 'PATCH',
        body,
      })
      onSuccess()
    } catch (e) {
      setErr(e?.message || 'Erreur lors de la correction')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Corriger la ligne</h2>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-slate-100">
            <XCircle className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600">
          Actuel : <span className="font-semibold text-slate-900">{line.product_name}</span>
          {' · '}{Number(line.qty_ordered || line.qty)} × {moneyXOF(line.unit_cost)}
        </div>

        {err && <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 font-medium">{err}</div>}

        <div className="relative">
          <label className="text-xs font-medium text-slate-600">Produit (laisser vide pour ne pas changer)</label>
          <input
            type="text"
            value={productSearch}
            onChange={e => { setProductSearch(e.target.value); setSelectedProduct(null) }}
            placeholder={line.product_name}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          {searching && <div className="absolute right-3 top-8 text-xs text-slate-400">Recherche…</div>}
          {productResults.length > 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
              {productResults.map(p => (
                <button key={p.id} onClick={() => selectProduct(p)}
                  className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 text-left">
                  <span className="font-medium text-slate-900">{p.name}</span>
                  <span className="text-slate-500 text-xs">stock: {p.stock_quantity ?? '?'}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Quantité reçue</label>
            <input type="number" value={qty} onChange={e => setQty(e.target.value)} min={1} step={1}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Coût unitaire (XOF)</label>
            <input type="number" value={unitCost} onChange={e => setUnitCost(e.target.value)} min={0}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
          </div>
        </div>

        <div className="text-right text-sm text-slate-500">
          Sous-total estimé : <span className="font-bold text-slate-900">{moneyXOF(Number(unitCost || 0) * Math.round(Number(qty || 1)))}</span>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium hover:bg-slate-50">Annuler</button>
          <button onClick={handleSubmit} disabled={submitting}
            className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
            {submitting ? 'Correction…' : 'Appliquer la correction'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* =========================================================
 * BA / RECEPTION (intégré dans Purchases.jsx)
 * ========================================================= */
function PurchasesBA({ products = [], reloadProducts }) {
  const [supplierName, setSupplierName]       = useState('')
  const [supplierContact, setSupplierContact] = useState('')
  const [supplierPhone, setSupplierPhone]     = useState('')
  const [pid, setPid]                         = useState('')
  const [qty, setQty]                         = useState(1)
  const [cost, setCost]                       = useState('')
  const [lines, setLines]                     = useState([])
  const [saving, setSaving]                   = useState(false)
  const [historyFilters, setHistoryFilters]   = useState({
    from: '',
    to: '',
    supplier: '',
    external_id: '',
    sort: 'date_desc',
  })
  const [historyRows, setHistoryRows]         = useState([])
  const [historyLoading, setHistoryLoading]   = useState(false)
  const [historyExporting, setHistoryExporting] = useState(false)

  // Expand détail + correction + suppression
  const [openRow, setOpenRow]         = useState(null)          // external_id du BA ouvert
  const [rowDetails, setRowDetails]   = useState({})            // cache: external_id → { lines, ... }
  const [loadingDetail, setLoadingDetail] = useState(null)      // external_id en cours de chargement
  const [correctingLine, setCorrectingLine] = useState(null)    // { externalId, line }
  const [deletingBA, setDeletingBA]   = useState(null)          // external_id en cours de suppression

  const user = useStore(s => s.user)
  const isAdmin = user?.roles?.some(r => ['admin', 'superadmin'].includes(r))
  const isOpsPlus = user?.roles?.some(r => ['ops', 'admin', 'superadmin'].includes(r))

  const inputBase =
    'w-full border border-border bg-surface-card rounded-lg px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-slate-200 focus:border-slate-300'
  const labelBase = 'text-xs font-medium text-content-secondary'
  const cardBase = 'bg-surface-card border border-border rounded-xl shadow-sm'

  const totalHT = useMemo(
    () => lines.reduce((s, l) => s + Number(l.qty) * Number(l.unit_cost), 0),
    [lines]
  )

  async function toggleRowDetail(externalId) {
    if (openRow === externalId) { setOpenRow(null); return }
    setOpenRow(externalId)
    if (rowDetails[externalId]) return
    setLoadingDetail(externalId)
    try {
      const data = await apiFetch(`/api/purchases/${externalId}`)
      setRowDetails(prev => ({ ...prev, [externalId]: data }))
    } catch (e) {
      alert('Erreur chargement détail : ' + (e?.message || String(e)))
      setOpenRow(null)
    } finally {
      setLoadingDetail(null)
    }
  }

  async function deleteBA(externalId) {
    if (!window.confirm(`Supprimer le bon d'achat ${externalId} ? Le stock sera reversé.`)) return
    setDeletingBA(externalId)
    try {
      await apiFetch(`/api/purchases/${externalId}`, { method: 'DELETE' })
      setRowDetails(prev => { const n = { ...prev }; delete n[externalId]; return n })
      setOpenRow(null)
      await loadPurchasesHistory(historyFilters)
    } catch (e) {
      alert('Erreur suppression : ' + (e?.message || String(e)))
    } finally {
      setDeletingBA(null)
    }
  }

  async function loadPurchasesHistory(nextFilters = historyFilters) {
    setHistoryLoading(true)
    try {
      const params = new URLSearchParams()
      if (nextFilters.from) params.set('from', nextFilters.from)
      if (nextFilters.to) params.set('to', nextFilters.to)
      if (nextFilters.supplier) params.set('supplier', nextFilters.supplier)
      if (nextFilters.external_id) params.set('external_id', nextFilters.external_id)
      if (nextFilters.sort) params.set('sort', nextFilters.sort)

      const data = await apiFetch(`/api/purchases?${params.toString()}`)
      setHistoryRows(Array.isArray(data) ? data : [])
    } catch (err) {
      alert('Erreur chargement historique achats : ' + (err?.message || String(err)))
      setHistoryRows([])
    } finally {
      setHistoryLoading(false)
    }
  }

  async function exportPurchasesExcel() {
    setHistoryExporting(true)
    try {
      const params = new URLSearchParams()
      if (historyFilters.from) params.set('from', historyFilters.from)
      if (historyFilters.to) params.set('to', historyFilters.to)
      if (historyFilters.supplier) params.set('supplier', historyFilters.supplier)
      if (historyFilters.external_id) params.set('external_id', historyFilters.external_id)
      if (historyFilters.sort) params.set('sort', historyFilters.sort)

      const blob = await apiFetchBlob(`/api/purchases/export.xlsx?${params.toString()}`)
      if (!blob || blob.size === 0) {
        alert('Aucune donnée pour cet export.')
        return
      }
      downloadBlob(blob, `export_achats_${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (err) {
      alert('Erreur export Excel achats : ' + (err?.message || String(err)))
    } finally {
      setHistoryExporting(false)
    }
  }

  useEffect(() => {
    loadPurchasesHistory(historyFilters)
  }, [])

  function addLine() {
    const p = products.find(x => String(x.id) === String(pid))
    if (!p) return alert('Choisir un produit')
    if (qty <= 0) return alert('Qté invalide')
    if (cost === '' || Number(cost) < 0) return alert('Coût unitaire invalide')

    setLines(l => [
      ...l,
      {
        id: Date.now() + Math.random(),
        productId: p.id,
        name: p.name,
        qty: Number(qty),
        unit_cost: Number(cost),
      }
    ])

    setPid('')
    setQty(1)
    setCost('')
  }

  function removeLine(id) {
    setLines(l => l.filter(x => x.id !== id))
  }

  async function createPurchase() {
    if (!supplierName) return alert('Fournisseur requis')
    if (lines.length === 0) return alert('Ajoute au moins un article')

    setSaving(true)
    try {
      const payload = {
        supplier_name: supplierName,
        supplier_phone: supplierPhone || null,
        supplier_contact: supplierContact || null,
        tva_pct: 18,
        lines: lines.map(l => ({
          product_id: l.productId,
          product_name: l.name,
          qty: l.qty,
          unit_cost: l.unit_cost,
        })),
      }

      const data = await apiFetch('/api/purchases', {
        method: 'POST',
        body: payload,
      })

      if (data?.external_id) {
        const blob = await apiFetchBlob(`/api/purchases/${data.external_id}/pdf`)
        forceDownload(blob, `${data.external_id}.pdf`)
      }

      if (typeof reloadProducts === 'function') {
        await reloadProducts()
      }

      await loadPurchasesHistory(historyFilters)

      setSupplierName('')
      setSupplierContact('')
      setSupplierPhone('')
      setPid('')
      setQty(1)
      setCost('')
      setLines([])

      alert('Achat enregistré + PDF téléchargé ✅')
    } catch (err) {
      alert('Erreur création achat : ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header KPI */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-extrabold text-content-primary">Réception / Bon d’achat (BA)</h2>
          <p className="text-sm text-content-secondary mt-1">
            Enregistre les réceptions réelles. Le stock augmente et le PDF est généré.
          </p>
        </div>

        <div className="text-right">
          <div className="text-xs text-content-secondary">Total HT</div>
          <div className="text-2xl font-extrabold text-content-primary">{money(totalHT)} XOF</div>
          <div className="text-xs text-content-muted mt-1">{lines.length} ligne(s)</div>
        </div>
      </div>

      {/* FOURNISSEUR */}
      <div className={`${cardBase} p-5 mb-5`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-content-primary">Fournisseur</h3>
          <span className="text-[11px] px-2 py-1 rounded-full bg-surface-primary text-content-secondary border border-border">
            BA = réception (stock bouge)
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className={labelBase}>Nom fournisseur *</div>
            <input
              value={supplierName}
              onChange={e => setSupplierName(e.target.value)}
              className={inputBase}
              placeholder="Nom fournisseur *"
            />
          </div>
          <div>
            <div className={labelBase}>Contact</div>
            <input
              value={supplierContact}
              onChange={e => setSupplierContact(e.target.value)}
              className={inputBase}
              placeholder="Contact"
            />
          </div>
          <div>
            <div className={labelBase}>Téléphone</div>
            <input
              value={supplierPhone}
              onChange={e => setSupplierPhone(e.target.value)}
              className={inputBase}
              placeholder="Téléphone"
            />
          </div>
        </div>
      </div>

      {/* ARTICLES */}
      <div className={`${cardBase} p-5`}>
        <h3 className="font-bold text-content-primary mb-4">Articles reçus</h3>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-6">
            <div className={labelBase}>Produit</div>
            <select
              value={pid}
              onChange={e => setPid(e.target.value)}
              className={inputBase}
            >
              <option value="">-- produit reçu --</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} · stock {p.stock_quantity}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <div className={labelBase}>Qté</div>
            <input
              type="number"
              min="1"
              value={qty}
              onChange={e => setQty(Number(e.target.value))}
              className={inputBase}
            />
          </div>

          <div className="md:col-span-2">
            <div className={labelBase}>PU achat (HT)</div>
            <input
              type="number"
              min="0"
              value={cost}
              onChange={e => setCost(e.target.value)}
              className={`${inputBase} text-right`}
              placeholder="PU achat (HT)"
            />
          </div>

          <div className="md:col-span-2">
            <button
              onClick={addLine}
              className="w-full px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] transition"
            >
              Ajouter
            </button>
          </div>
        </div>

        {/* Lignes */}
        <div className="mt-5 overflow-auto border border-border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary text-content-secondary">
              <tr>
                <th className="p-3 text-left font-semibold">Produit</th>
                <th className="p-3 text-right font-semibold">Qté</th>
                <th className="p-3 text-right font-semibold">PU (HT)</th>
                <th className="p-3 text-right font-semibold">Total</th>
                <th className="p-3 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-content-secondary">
                    Aucune ligne de réception.
                  </td>
                </tr>
              ) : (
                lines.map((l, idx) => (
                  <tr
                    key={l.id}
                    className={`border-t border-border-light ${idx % 2 === 0 ? 'bg-surface-card' : 'bg-surface-primary/40'} hover:bg-slate-50/40 transition`}
                  >
                    <td className="p-3 font-medium text-content-primary">{l.name}</td>
                    <td className="p-3 text-right font-semibold">{l.qty}</td>
                    <td className="p-3 text-right">{money(l.unit_cost)} XOF</td>
                    <td className="p-3 text-right font-extrabold">
                      {money(l.qty * l.unit_cost)} XOF
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => removeLine(l.id)}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-100 transition"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={createPurchase}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition ${
              saving ? 'bg-slate-300 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-950 active:scale-[0.99]'
            }`}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer l’achat + PDF'}
          </button>
        </div>
      </div>

      <div className={`${cardBase} p-5 mt-5`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-4">
          <div>
            <h3 className="font-bold text-content-primary">Historique des achats</h3>
            <p className="text-sm text-content-secondary mt-1">
              Filtrez, triez puis exportez les achats réels au format Excel.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadPurchasesHistory(historyFilters)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-border hover:bg-surface-primary"
            >
              Actualiser
            </button>
            <button
              onClick={exportPurchasesExcel}
              disabled={historyExporting}
              className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition ${
                historyExporting ? 'bg-slate-300 cursor-not-allowed' : 'bg-slate-900 hover:bg-black'
              }`}
            >
              {historyExporting ? 'Export…' : 'Exporter Excel'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mb-4">
          <div className="md:col-span-2">
            <div className={labelBase}>Du</div>
            <input
              type="date"
              value={historyFilters.from}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, from: e.target.value }))}
              className={inputBase}
            />
          </div>
          <div className="md:col-span-2">
            <div className={labelBase}>Au</div>
            <input
              type="date"
              value={historyFilters.to}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, to: e.target.value }))}
              className={inputBase}
            />
          </div>
          <div className="md:col-span-3">
            <div className={labelBase}>Fournisseur</div>
            <input
              value={historyFilters.supplier}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, supplier: e.target.value }))}
              className={inputBase}
              placeholder="Nom fournisseur"
            />
          </div>
          <div className="md:col-span-2">
            <div className={labelBase}>Référence</div>
            <input
              value={historyFilters.external_id}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, external_id: e.target.value }))}
              className={inputBase}
              placeholder="PO-..."
            />
          </div>
          <div className="md:col-span-2">
            <div className={labelBase}>Tri</div>
            <select
              value={historyFilters.sort}
              onChange={(e) => setHistoryFilters((prev) => ({ ...prev, sort: e.target.value }))}
              className={inputBase}
            >
              <option value="date_desc">Date décroissante</option>
              <option value="date_asc">Date croissante</option>
              <option value="supplier_asc">Fournisseur A-Z</option>
              <option value="supplier_desc">Fournisseur Z-A</option>
              <option value="amount_desc">Montant décroissant</option>
              <option value="amount_asc">Montant croissant</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <button
              onClick={() => loadPurchasesHistory(historyFilters)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold border border-border hover:bg-surface-primary"
            >
              OK
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary text-content-secondary">
              <tr>
                <th className="p-3 text-left font-semibold">Référence</th>
                <th className="p-3 text-left font-semibold">Date</th>
                <th className="p-3 text-left font-semibold">Fournisseur</th>
                <th className="p-3 text-right font-semibold">Articles</th>
                <th className="p-3 text-right font-semibold">Total HT</th>
                <th className="p-3 text-right font-semibold">TVA</th>
                <th className="p-3 text-right font-semibold">Total TTC</th>
                <th className="p-3 text-center font-semibold w-8"></th>
              </tr>
            </thead>
            <tbody>
              {!historyLoading && historyRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-content-secondary">
                    Aucun achat pour ces filtres.
                  </td>
                </tr>
              ) : (
                historyRows.map((row, idx) => {
                  const extId = row.external_id
                  const isOpen = openRow === extId
                  const detail = rowDetails[extId]
                  const isLoadingThis = loadingDetail === extId
                  const isDeletingThis = deletingBA === extId

                  return (
                    <React.Fragment key={row.id || extId || idx}>
                      <tr
                        className={`border-t border-border-light ${idx % 2 === 0 ? 'bg-surface-card' : 'bg-surface-primary/40'} hover:bg-slate-50/40 transition cursor-pointer`}
                        onClick={() => toggleRowDetail(extId)}
                      >
                        <td className="p-3 font-semibold text-content-primary">{extId}</td>
                        <td className="p-3 text-content-secondary">
                          {row.created_at ? String(row.created_at).slice(0, 10) : '—'}
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-content-primary">{row.supplier_name || '—'}</div>
                          <div className="text-xs text-content-secondary">{row.supplier_phone || '—'}</div>
                        </td>
                        <td className="p-3 text-right font-semibold">{Number(row.items_count || 0)}</td>
                        <td className="p-3 text-right">{money(row.total_ht || 0)} XOF</td>
                        <td className="p-3 text-right">{money(row.total_tva || 0)} XOF</td>
                        <td className="p-3 text-right font-extrabold text-content-primary">{money(row.total_ttc || 0)} XOF</td>
                        <td className="p-3 text-center">
                          {isLoadingThis
                            ? <span className="text-xs text-slate-400">…</span>
                            : isOpen
                              ? <ChevronUp className="h-4 w-4 text-slate-400 mx-auto" />
                              : <ChevronDown className="h-4 w-4 text-slate-400 mx-auto" />
                          }
                        </td>
                      </tr>

                      {/* Panneau lignes dépliable */}
                      {isOpen && (
                        <tr className="border-t border-amber-100">
                          <td colSpan={8} className="bg-amber-50 p-4">
                            {isLoadingThis || !detail ? (
                              <div className="text-sm text-slate-500 text-center py-2">Chargement…</div>
                            ) : (
                              <div className="space-y-2">
                                {/* Lignes produits */}
                                {(detail.lines || []).length === 0 ? (
                                  <div className="text-sm text-slate-500">Aucune ligne.</div>
                                ) : (
                                  <div className="space-y-1">
                                    {(detail.lines || []).map((l, li) => (
                                      <div key={l.id || li}
                                        className="flex items-center justify-between rounded-xl bg-white p-3 border border-amber-200">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-slate-900">{l.product_name} × {l.qty}</div>
                                          <div className="text-xs text-slate-500">{moneyXOF(l.unit_cost)} / unité</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-semibold text-slate-900">{moneyXOF(l.line_total || Number(l.qty) * Number(l.unit_cost))}</span>
                                          {isOpsPlus && l.id && (
                                            <button
                                              onClick={e => { e.stopPropagation(); setCorrectingLine({ externalId: extId, line: { ...l, qty_ordered: l.qty } }) }}
                                              title="Corriger cette ligne"
                                              className="rounded-lg p-1.5 text-slate-400 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                                            >
                                              <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Bouton supprimer le BA */}
                                {isAdmin && (
                                  <div className="flex justify-end pt-1">
                                    <button
                                      onClick={e => { e.stopPropagation(); deleteBA(extId) }}
                                      disabled={isDeletingThis}
                                      className="inline-flex items-center gap-1.5 rounded-xl bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                      {isDeletingThis ? 'Suppression…' : 'Supprimer ce bon d\'achat'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal correction ligne */}
      {correctingLine && (
        <CorrectPurchaseLineModal
          externalId={correctingLine.externalId}
          line={correctingLine.line}
          onClose={() => setCorrectingLine(null)}
          onSuccess={() => {
            setCorrectingLine(null)
            // Invalider cache du détail pour forcer rechargement
            setRowDetails(prev => { const n = { ...prev }; delete n[correctingLine.externalId]; return n })
            toggleRowDetail(correctingLine.externalId)
            loadPurchasesHistory(historyFilters)
          }}
        />
      )}
    </div>
  )
}
